<template>
  <span ref="reference">
    <slot name="default" />
  </span>
  <span ref="floating" class="floating" :style="floatingStyles">
    <slot name="content" />
  </span>
</template>

<script setup lang="ts">
import { useFloating, offset, size, shift } from '@floating-ui/vue'

const props = defineProps({
  reduceHeight: {
    type: [Number, String],
    default: 100
  }
})

const reference = ref() as Ref<HTMLElement|null>
const floating = ref() as Ref<HTMLElement|null>
const { floatingStyles, update } = useFloating(reference, floating, {
  placement: 'bottom',
  middleware: [
    offset(10),
    shift(),
    size({
      apply ({ availableWidth, availableHeight, elements }) {
        // Change styles, e.g.
        Object.assign(elements.floating.style, {
          maxWidth: `${Math.max(0, availableWidth)}px`,
          maxHeight: `${Math.max(window.innerHeight - props.reduceHeight, Math.min(availableHeight, window.innerHeight - props.reduceHeight))}px`
        })
      }
    })
  ]
})

const handleClickOutside = (event) => {
  if (event.type === 'mouseup' && !floating.value?.contains(event.target) && !reference.value?.contains(event.target)) {
    hide()
  } else if (event.type === 'keydown' && event.key === 'Escape') {
    hide()
  }
}

const show = () => {
  if (floating.value) {
    floating.value.style.display = 'flex'
  }
  update()
  document.addEventListener('mouseup', handleClickOutside)
  document.addEventListener('keydown', handleClickOutside)
  window.addEventListener('resize', update)
  window.addEventListener('scroll', update)
}

const hide = (reason?: string) => {
  if (floating.value) {
    floating.value.style.display = ''
  }
  document.removeEventListener('mouseup', handleClickOutside)
  document.removeEventListener('keydown', handleClickOutside)
  window.removeEventListener('resize', update)
  window.removeEventListener('scroll', update)
  emit('hidden', reason)
}

defineExpose({
  show,
  hide
})

let resizeObserver
let intersectionObserver

onMounted(() => {
  let isFullyVisible = false

  intersectionObserver = new IntersectionObserver((entries) => {
    isFullyVisible = entries[0].isIntersecting
  }, { threshold: 1 })

  intersectionObserver.observe(floating.value)

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const fitsIntoViewport = entry.contentRect.width <= window.innerWidth && entry.contentRect.height <= window.innerHeight
      setTimeout(() => {
        if (!isFullyVisible && fitsIntoViewport) {
          entry.target.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
      }, 100)
    }
  })

  resizeObserver.observe(floating.value)
})

onBeforeUnmount(() => {
  resizeObserver.unobserve(floating.value)
  intersectionObserver.unobserve(floating.value)
})

const emit = defineEmits(['hidden'])

</script>

<style scoped>
.floating {
  background-color: #FFFFFF;
  border-color: rgb(228 228 228 / 1);
  border-width: 1px;
  border-style: solid;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  z-index: 100;
  display: none;
}
</style>
